/* eslint-disable react/no-unstable-nested-components */
import React, {
  useState,
  useContext,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  CardBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useQuery, useLazyQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  RiArrowDownSLine,
  RiDownload2Line,
  RiTimeLine,
  RiNotification3Line,
} from 'react-icons/ri';
import Select from 'react-select';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import Heading from 'components/Heading';
import CustomTabs from 'components/CustomTabs';
import FSBModal from 'components/common/FSBModal';
import DeleteConfirmationAlert from 'components/DeleteConfirmationAlert';
import AdminNotificationDropdown from 'components/Sidebar/AdminNotificationDropdown';
import Loader from 'components/common/Loader';
import { authContext } from 'contexts/AuthContext';
import { db } from 'config/firebase';
import { STAFF_LIST_QUERY } from 'pages/Staff/StaffgraphQL';
import {
  GET_USER_INFO,
  GET_ROLES_WITH_PERMISSIONS,
} from 'pages/Login/LoginGraphQL';
import { defaultDeleteFilter } from 'utils/constants';
import { axiosGetAPI, axiosPost, axiosUpdate, axiosDelete } from 'services';
import { APP_TOKEN } from '../../constants';
import './Task.css';

const TASK_COLUMNS = [
  { key: 'pending', title: 'Pending', tone: 'pending' },
  { key: 'completed', title: 'Completed', tone: 'completed' },
];

const EMPTY_TASKS = {
  pending: [],
  completed: [],
};

const STATUS_TO_KEY = {
  Pending: 'pending',
  Completed: 'completed',
};

const STATUS_LABELS = {
  pending: 'Pending',
  completed: 'Completed',
};
const STATUS_API_VALUES = {
  pending: 'pending',
  completed: 'completed',
};
const getTaskOwnershipTabs = (canViewEscalationTasks) => {
  const tabs = [
    { key: 'assigned', label: 'Assigned to me', queryKey: 'assignedToId' },
    { key: 'created', label: 'Assigned by me', queryKey: 'createdBy' },
  ];

  if (canViewEscalationTasks) {
    tabs.push({
      key: 'escalated',
      label: 'Escalation Task',
      queryKey: 'escalated',
      endpoint: 'tasks/escalated',
    });
  }

  return tabs;
};
const TASK_NOTIFICATION_DELIVERY_WINDOW_MS = 2 * 60 * 1000;
const TASK_SIDEBAR_BG = '#282726';

const taskAssigneeSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    height: 36,
    borderRadius: 12,
    borderColor: state.isFocused ? TASK_SIDEBAR_BG : '#cfd7e3',
    boxShadow: 'none',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    marginRight: 8,
    ':hover': {
      borderColor: TASK_SIDEBAR_BG,
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 10px 0 12px',
    height: 36,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 12,
    fontWeight: 600,
    color: '#1f2937',
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 12,
    color: '#64748b',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 36,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: '0 10px 0 0',
    color: state.isFocused ? TASK_SIDEBAR_BG : '#64748b',
    ':hover': {
      color: TASK_SIDEBAR_BG,
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    marginTop: 8,
    overflow: 'hidden',
    border: '1px solid #d8e2ee',
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 3000,
  }),
  menuList: (base) => ({
    ...base,
    padding: 4,
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    fontSize: 12,
    padding: '8px 12px',
    cursor: 'pointer',
    backgroundColor: (() => {
      if (state.isSelected) return TASK_SIDEBAR_BG;
      if (state.isFocused) return 'rgba(40, 39, 38, 0.08)';
      return base.backgroundColor;
    })(),
    color: state.isSelected ? '#ffffff' : '#1f2937',
    ':active': {
      backgroundColor: TASK_SIDEBAR_BG,
      color: '#ffffff',
    },
  }),
};

const getAssignedToIdFromStorage = () => {
  try {
    const stored = localStorage.getItem(APP_TOKEN);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return (
      parsed?.userId ||
      parsed?.id ||
      parsed?.user_id ||
      parsed?.user?.id ||
      parsed?.user?.userId ||
      parsed?.staffId ||
      parsed?.staff?.id ||
      null
    );
  } catch (error) {
    return null;
  }
};

const normalizeTaskIdentifier = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = `${value}`.trim();
  return normalized || '';
};

const normalizeTextForMatch = (value = '') =>
  `${value || ''}`.trim().replace(/\s+/g, ' ').toLowerCase();

const getTaskAssignedToId = (task) => {
  const rawValue =
    task?.assignedToId ??
    task?.assigned_to_id ??
    task?.assigneeId ??
    task?.assignedToUserId ??
    task?.assignedTo?.id ??
    task?.assignedTo?.userId ??
    task?.assignedToUser?.id ??
    task?.assignedToUser?.userId ??
    task?.recipientUserId;
  const normalized = normalizeTaskIdentifier(rawValue);
  return normalized || null;
};

const getTaskCreatorId = (task) => {
  const rawValue =
    task?.createdByUserId ??
    task?.created_by_user_id ??
    task?.createdById ??
    task?.created_by_id ??
    task?.createdBy?.id ??
    task?.createdBy?.userId ??
    task?.createdByUser?.id ??
    task?.createdByUserId ??
    task?.createdByUser?.userId ??
    task?.createdBy;
  const normalized = normalizeTaskIdentifier(rawValue);
  return normalized || null;
};

const getCurrentUserIdentityCandidates = (authData) => {
  const identifiers = [
    authData?.userId,
    authData?.id,
    authData?.user_id,
    authData?.user?.id,
    authData?.user?.userId,
    authData?.staffId,
    authData?.staff?.id,
    authData?.employeeId,
    authData?.employee?.id,
    getAssignedToIdFromStorage(),
  ];
  return Array.from(
    new Set(identifiers.map(normalizeTaskIdentifier).filter(Boolean)),
  );
};

const taskMatchesCurrentUser = (task, authData) => {
  const userIds = getCurrentUserIdentityCandidates(authData);
  if (!userIds.length) return false;

  const taskAssigneeIds = [
    task?.assignedToId,
    task?.assigned_to_id,
    task?.assigneeId,
    task?.assignedToUserId,
    task?.assignedTo?.id,
    task?.assignedTo?.userId,
    task?.assignedToUser?.id,
    task?.assignedToUser?.userId,
    task?.recipientUserId,
  ]
    .map(normalizeTaskIdentifier)
    .filter(Boolean);

  if (taskAssigneeIds.some((value) => userIds.includes(value))) return true;

  const authNames = [
    [authData?.firstName, authData?.lastName].filter(Boolean).join(' '),
    authData?.fullName,
    authData?.name,
    authData?.email,
  ]
    .map(normalizeTextForMatch)
    .filter(Boolean);

  if (!authNames.length) return false;

  const taskAssigneeNames = [
    task?.assignedToName,
    task?.assignee,
    task?.assignedTo?.name,
    task?.assignedTo?.fullName,
    task?.assignedToUser?.name,
    task?.assignedToUser?.fullName,
    task?.recipientName,
  ]
    .map(normalizeTextForMatch)
    .filter(Boolean);

  return taskAssigneeNames.some((value) => authNames.includes(value));
};

const buildTaskNotificationDocId = ({ recipientUserId, taskId }) => {
  const safeRecipient = `${recipientUserId || 'user'}`.replace(
    /[^a-zA-Z0-9_-]/g,
    '',
  );
  const safeTaskId = `${taskId || 'task'}`.replace(/[^a-zA-Z0-9_-]/g, '');
  if (!taskId) {
    return `${safeRecipient}_task_${Date.now()}`;
  }
  return `${safeRecipient}_task_${safeTaskId}`;
};

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const STAFF_FILTERS = {
  filter: [
    {
      key: 'userSearchFilter',
      value: '',
      type: 'STRING',
      op: 'LIKE',
    },
    {
      key: 'name',
      value: 'TRADER',
      setAttribute: 'roles',
      type: 'STRING',
      op: 'NOT_IN',
    },
    { ...defaultDeleteFilter },
    {
      key: 'userSearchFilter',
      value: '',
      type: 'STRING',
      op: 'LIKE',
    },
  ],
  pageNo: 1,
  pageSize: 10,
  sortCol: 'id',
  sortDirection: 'DESC',
  pagination: false,
};

const formatDisplayDate = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${MONTHS[date.getMonth()]} ${day}, ${date.getFullYear()}`;
};

const parseTimestampToDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numValue = Number(value);
  if (!Number.isNaN(numValue)) {
    const ms = numValue > 1e12 ? numValue : numValue * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getTimestampMs = (value) => {
  const date = parseTimestampToDate(value);
  return date ? date.getTime() : null;
};

const getTaskDeadlineMs = (task, kind) => {
  const directTimeCandidates =
    kind === 'ert'
      ? [
        task?.ertEndTimeMs,
        task?.ertEndTime,
        task?.ertEndAtMs,
        task?.ertEndAt,
        task?.estimatedResolutionEndTime,
      ]
      : [
        task?.escalationAtMs,
        task?.escalationAt,
        task?.reminderEndTimeMs,
        task?.reminderEndTime,
      ];

  const directTimeMatch = directTimeCandidates
    .map((candidate) => getTimestampMs(candidate))
    .find((ms) => ms !== null);

  if (directTimeMatch !== undefined) return directTimeMatch;

  if (kind === 'reminder') {
    const ertDeadlineMs = getTaskDeadlineMs(task, 'ert');
    const reminderMinutes = Number(task?.reminderMinutes);
    if (
      ertDeadlineMs !== null &&
      Number.isFinite(reminderMinutes) &&
      reminderMinutes > 0
    ) {
      return ertDeadlineMs + reminderMinutes * 60 * 1000;
    }
  }

  const createdAtMs = getTimestampMs(
    task?.createdAtMs ?? task?.createdAtValue ?? task?.createdAt,
  );
  const minutesKey = kind === 'ert' ? 'ertMinutes' : 'reminderMinutes';
  const minutes = Number(task?.[minutesKey]);
  if (createdAtMs !== null && Number.isFinite(minutes) && minutes > 0) {
    if (kind === 'reminder') {
      const ertMinutes = Number(task?.ertMinutes);
      if (Number.isFinite(ertMinutes) && ertMinutes > 0) {
        return createdAtMs + (ertMinutes + minutes) * 60 * 1000;
      }
    }
    return createdAtMs + minutes * 60 * 1000;
  }

  return null;
};

const formatCountdownValue = (deadlineMs, nowMs) => {
  if (!deadlineMs) return '-';
  const remainingMs = deadlineMs - nowMs;
  if (remainingMs <= 0) return 'Expired';

  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (value) => `${value}`.padStart(2, '0');
  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
};

const getTaskCountdownLabel = (task, nowMs, kind) =>
  formatCountdownValue(getTaskDeadlineMs(task, kind), nowMs);

const getTaskReminderCountdownLabel = (task, nowMs) => {
  const ertDeadlineMs = getTaskDeadlineMs(task, 'ert');
  if (!ertDeadlineMs) return '-';
  if (nowMs < ertDeadlineMs) return '-';

  const reminderDeadlineMs = getTaskDeadlineMs(task, 'reminder');
  return formatCountdownValue(reminderDeadlineMs, nowMs);
};

const isDeletedValue = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
};

const normalizePriority = (value = '') => {
  const upper = `${value}`.toUpperCase();
  if (upper === 'HIGH') return 'High';
  if (upper === 'LOW') return 'Low';
  return 'Medium';
};

const normalizeStatusKey = (value = '') => {
  const upper = `${value}`.replace(/_/g, ' ').toUpperCase();
  if (upper === 'COMPLETED' || upper === 'DONE') return 'completed';
  if (upper === 'PENDING') return 'pending';
  return 'pending';
};

const resolveCommentCount = (task) => {
  if (!task) return 0;
  if (Array.isArray(task?.comments)) return task.comments.length;
  const raw =
    task?.commentCount ??
    task?.commentsCount ??
    task?.comments_count ??
    task?.comment_count ??
    task?.commentTotal ??
    0;
  const num = Number(raw);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.floor(num));
};

const getApiListData = (response) => {
  const rawData = response?.data;
  if (Array.isArray(rawData)) return rawData;
  if (Array.isArray(rawData?.data)) return rawData.data;
  if (Array.isArray(rawData?.items)) return rawData.items;
  if (Array.isArray(rawData?.content)) return rawData.content;
  return [];
};

const applyCommentCountsToTasks = (tasksByStatus, countMap = {}) => {
  if (!tasksByStatus) return tasksByStatus;
  let changed = false;
  const next = Object.keys(tasksByStatus).reduce((acc, key) => {
    acc[key] = (tasksByStatus[key] || []).map((task) => {
      const sourceId = task?.sourceId;
      if (sourceId === null || sourceId === undefined) return task;
      const mapKey = `${sourceId}`;
      let mapped = null;
      if (Object.prototype.hasOwnProperty.call(countMap, mapKey)) {
        mapped = countMap[mapKey];
      } else if (Object.prototype.hasOwnProperty.call(countMap, sourceId)) {
        mapped = countMap[sourceId];
      }
      if (mapped === null || mapped === undefined) return task;
      const safeCount = Number.isFinite(Number(mapped))
        ? Math.max(0, Number(mapped))
        : 0;
      if (task.commentCount === safeCount) return task;
      changed = true;
      return { ...task, commentCount: safeCount };
    });
    return acc;
  }, {});
  return changed ? next : tasksByStatus;
};

const normalizePermissionName = (value) => {
  if (!value) return '';
  return `${value}`.trim().replace(/\s+/g, '_').toUpperCase();
};

const collectPermissionNames = (authData) => {
  const permissionNames = new Set();
  const directPermissions = Array.isArray(authData?.permissions)
    ? authData.permissions
    : [];
  directPermissions.forEach((permission) => {
    const normalized = normalizePermissionName(
      permission?.permissionName || permission?.name || permission,
    );
    if (normalized) permissionNames.add(normalized);
  });

  const roles = Array.isArray(authData?.roles) ? authData.roles : [];
  roles.forEach((role) => {
    const rolePermissions = Array.isArray(role?.permissions)
      ? role.permissions
      : [];
    rolePermissions.forEach((permission) => {
      const normalized = normalizePermissionName(
        permission?.permissionName || permission?.name || permission,
      );
      if (normalized) permissionNames.add(normalized);
    });
  });

  return permissionNames;
};

// FIX 1: Moved getNextTaskId ABOVE buildTasksByStatusFromApi so it's defined before use
const getNextTaskId = (tasks) => {
  const allTasks = Object.values(tasks).flat();
  const max = allTasks.reduce((acc, task) => {
    const match = `${task.id}`.match(/(\d+)/);
    const taskValue = match ? Number(match[1]) : 0;
    return Math.max(acc, taskValue);
  }, 0);
  return `T-${max + 1}`;
};

const buildTasksByStatusFromApi = (apiTasks) => {
  const buckets = {
    pending: [],
    completed: [],
  };
  const normalized = (apiTasks || [])
    .map((task) => {
      const createdDate = parseTimestampToDate(task?.createdAt);
      const dueDate = parseTimestampToDate(task?.dueDate);
      const createdById = getTaskCreatorId(task);
      const assignedToId = getTaskAssignedToId(task);
      const statusKey = normalizeStatusKey(task?.status);
      return {
        id: task?.id ? `T-${task.id}` : getNextTaskId(buckets),
        sourceId: task?.id,
        title: task?.title || 'Untitled task',
        description: task?.description || '',
        assignee: task?.assignedToName || 'Unassigned',
        createdById,
        createdByName: task?.createdByName || '',
        assignedToId,
        phoneNumber: task?.phoneNumber || '',
        gradeName: task?.gradeName || '',
        dueDate: dueDate ? formatDisplayDate(dueDate) : '',
        dueDateMs: dueDate ? dueDate.getTime() : null,
        createdAt: createdDate ? formatDisplayDate(createdDate) : '',
        createdAtMs: createdDate ? createdDate.getTime() : null,
        category: task?.category || 'General',
        priority: normalizePriority(task?.priority),
        commentCount: resolveCommentCount(task),
        isDeleted: isDeletedValue(task?.isDeleted),
        statusKey,
        createdAtValue: createdDate ? createdDate.getTime() : 0,
        ertMinutes: task?.ertMinutes ?? '',
        reminderMinutes: task?.reminderMinutes ?? '',
        ertEndTime: task?.ertEndTime ?? task?.ertEndAt ?? '',
        reminderAt: task?.reminderAt ?? task?.reminderEndTime ?? '',
        escalationAt: task?.escalationAt ?? task?.escalationEndTime ?? '',
        ertEndTimeMs: getTimestampMs(
          task?.ertEndTimeMs ??
          task?.ertEndTime ??
          task?.ertEndAtMs ??
          task?.ertEndAt,
        ),
        reminderAtMs: getTimestampMs(
          task?.reminderAtMs ??
          task?.reminderAt ??
          task?.reminderEndTimeMs ??
          task?.reminderEndTime,
        ),
        escalationAtMs: getTimestampMs(
          task?.escalationAtMs ??
          task?.escalationAt ??
          task?.escalationEndTimeMs ??
          task?.escalationEndTime,
        ),
      };
    })
    .sort((a, b) => b.createdAtValue - a.createdAtValue);

  normalized.forEach((task) => {
    const { statusKey, createdAtValue, ...rest } = task;
    const columnKey = buckets[statusKey] ? statusKey : 'pending';
    buckets[columnKey].push(rest);
  });

  return buckets;
};

const findCreatedTaskMatch = ({
  apiTasks = [],
  createdByValue,
  assignedToId,
  title,
  description,
  dueDate,
}) => {
  if (!Array.isArray(apiTasks) || !apiTasks.length) return null;

  const normalizedTitle = normalizeTextForMatch(title);
  const normalizedDescription = normalizeTextForMatch(description);
  const targetDueDate = dueDate ? new Date(dueDate).toISOString() : '';

  const candidates = apiTasks
    .filter((task) => {
      const taskTitle = normalizeTextForMatch(task?.title);
      const taskDescription = normalizeTextForMatch(task?.description);
      const taskAssignedToId = getTaskAssignedToId(task);
      const taskCreatedById = getTaskCreatorId(task);
      const taskDueDate = task?.dueDate
        ? new Date(task.dueDate).toISOString()
        : '';

      if (normalizedTitle && taskTitle !== normalizedTitle) return false;
      if (normalizedDescription && taskDescription !== normalizedDescription)
        return false;
      if (
        assignedToId !== null &&
        normalizeTaskIdentifier(taskAssignedToId) !==
        normalizeTaskIdentifier(assignedToId)
      )
        return false;
      if (
        Number.isFinite(createdByValue) &&
        normalizeTaskIdentifier(taskCreatedById) !==
        normalizeTaskIdentifier(createdByValue)
      )
        return false;
      if (targetDueDate && taskDueDate !== targetDueDate) return false;
      return true;
    })
    .sort((left, right) => {
      const leftTime = parseTimestampToDate(left?.createdAt)?.getTime?.() || 0;
      const rightTime =
        parseTimestampToDate(right?.createdAt)?.getTime?.() || 0;
      return rightTime - leftTime;
    });

  return candidates[0] || null;
};

const TaskBoard = ({
  embedded = false,
  embeddedTitle = 'Task Listing',
  initialTaskId = null,
  initialOwnershipTab = null,
  detailOnly = false,
  onInitialTaskClose,
} = {}) => {
  const { auth, setAuthData } = useContext(authContext);
  const permissionNames = useMemo(
    () => collectPermissionNames(auth?.data),
    [auth?.data],
  );
  const canViewEscalationTasks = useMemo(
    () =>
      Array.isArray(auth?.data?.roles) &&
      auth.data.roles.some(
        (role) => role?.name === 'SUPER_ADMIN' || role?.name === 'ADMIN',
      ),
    [auth?.data?.roles],
  );
  const taskOwnershipTabs = useMemo(
    () => getTaskOwnershipTabs(canViewEscalationTasks),
    [canViewEscalationTasks],
  );
  const canAssignTask = permissionNames.has('ASSIGN_TASK_TO_USER');
  const canCreateTask = permissionNames.has('CREATE_TASK');
  const isEmbedded = Boolean(embedded);
  const [hasLoadedTasks, setHasLoadedTasks] = useState(false);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [tasksByStatus, setTasksByStatus] = useState(EMPTY_TASKS);
  const [currentTimeMs, setCurrentTimeMs] = useState(() => Date.now());
  const tasksByStatusRef = useRef(EMPTY_TASKS);
  const commentCountCacheRef = useRef({});
  const authRefreshInFlightRef = useRef(false);
  const lastAuthRefreshAtRef = useRef(0);
  const [draggedTask, setDraggedTask] = useState(null);
  const [activeColumn, setActiveColumn] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const location = useLocation();
  const [ownershipTab, setOwnershipTab] = useState(
    initialOwnershipTab || location?.state?.ownershipTab || 'assigned',
  );

  useEffect(() => {
    if (initialOwnershipTab) {
      setOwnershipTab(initialOwnershipTab);
    } else if (location?.state?.ownershipTab) {
      setOwnershipTab(location.state.ownershipTab);
    }
  }, [initialOwnershipTab, location?.state?.ownershipTab]);
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(
    Boolean(initialTaskId),
  );
  const [filters] = useState({
    created: 'all',
    due: 'all',
    category: 'all',
    status: 'all',
  });
  const [taskForm, setTaskForm] = useState({
    title: '',
    details: '',
    setDueDate: false,
    dueDate: '',
    repeatEnabled: false,
    repeatInterval: '',
    repeatEvery: '',
    assignUser: false,
    assignee: '',
  });
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState('');
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [markedAsReadTaskIds, setMarkedAsReadTaskIds] = useState({});
  const [markingTaskId, setMarkingTaskId] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const commentDraftRef = useRef('');
  const fetchCommentsRef = useRef(null);
  const lastCommentTaskIdRef = useRef(null);
  const taskNotificationInitRef = useRef(false);
  const knownTaskIdsRef = useRef(new Set());
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [commentAttachment, setCommentAttachment] = useState(null);
  const [commentAttachmentUrl, setCommentAttachmentUrl] = useState('');
  const [commentPreviewUrl, setCommentPreviewUrl] = useState('');
  const [commentUploadLoading, setCommentUploadLoading] = useState(false);
  const [commentUploadError, setCommentUploadError] = useState('');
  const commentFileInputRef = useRef(null);
  const [commentImagePreviewUrl, setCommentImagePreviewUrl] = useState('');
  const [isCommentImagePreviewOpen, setIsCommentImagePreviewOpen] =
    useState(false);
  const [deleteTaskTarget, setDeleteTaskTarget] = useState(null);
  const [showDeleteRecord, setShowDeleteRecord] = useState(false);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Infinite scroll ref - declared early to avoid hoisting issues
  const loadMoreTasksRef = useRef(null);

  const clearCommentAttachment = useCallback(() => {
    setCommentAttachment(null);
    setCommentAttachmentUrl('');
    setCommentPreviewUrl('');
    setCommentUploadError('');
    if (commentFileInputRef.current) {
      commentFileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    const tick = () => setCurrentTimeMs(Date.now());
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const [fetchUserInfo] = useLazyQuery(GET_USER_INFO, {
    fetchPolicy: 'network-only',
  });
  const [fetchRolesWithPermissions] = useLazyQuery(GET_ROLES_WITH_PERMISSIONS, {
    fetchPolicy: 'network-only',
  });

  const updateTaskCommentCount = useCallback((taskId, count) => {
    if (!taskId) return;
    const safeCount = Number.isFinite(Number(count))
      ? Math.max(0, Number(count))
      : 0;
    commentCountCacheRef.current[`${taskId}`] = safeCount;
    setTasksByStatus((prev) => {
      let changed = false;
      const next = Object.keys(prev).reduce((acc, key) => {
        acc[key] = (prev[key] || []).map((task) => {
          if (`${task.sourceId}` !== `${taskId}`) return task;
          if (task.commentCount === safeCount) return task;
          changed = true;
          return { ...task, commentCount: safeCount };
        });
        return acc;
      }, {});
      return changed ? next : prev;
    });
  }, []);

  const fetchCommentCountsForTasks = useCallback(
    async (apiTasks = []) => {
      if (!Array.isArray(apiTasks) || !apiTasks.length) return;
      const hasCountInApi = apiTasks.some(
        (task) =>
          task?.commentCount !== undefined ||
          task?.commentsCount !== undefined ||
          task?.comments_count !== undefined ||
          Array.isArray(task?.comments),
      );

      if (hasCountInApi) {
        apiTasks.forEach((task) => {
          if (!task?.id) return;
          commentCountCacheRef.current[`${task.id}`] =
            resolveCommentCount(task);
        });
        return;
      }

      const taskIds = Array.from(
        new Set(apiTasks.map((task) => task?.id).filter(Boolean)),
      );
      const pending = taskIds.filter(
        (id) => commentCountCacheRef.current[`${id}`] === undefined,
      );
      if (!pending.length) return;

      const concurrency = 5;
      let index = 0;
      const fetchOne = (taskId) =>
        axiosGetAPI(
          `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/${taskId}/comments`,
          auth,
        ).then((response) => {
          if (response?.isSuccessful) {
            const apiComments = response?.data?.data || [];
            const count = Array.isArray(apiComments) ? apiComments.length : 0;
            updateTaskCommentCount(taskId, count);
          }
        });

      const run = () => {
        if (index >= pending.length) return Promise.resolve();
        const taskId = pending[index];
        index += 1;
        return fetchOne(taskId).then(run);
      };

      await Promise.all(
        Array.from({ length: Math.min(concurrency, pending.length) }, () =>
          run(),
        ),
      );
    },
    [auth, updateTaskCommentCount],
  );
  const tasksFetchInFlight = useRef(false);
  const pendingFetchRef = useRef(false);
  const lastFetchAtRef = useRef(0);
  const lastAuthTokenRef = useRef(null);
  const lastOwnershipTabRef = useRef(ownershipTab);

  const {
    data: staffData,
    loading: staffLoading,
    error: staffError,
  } = useQuery(STAFF_LIST_QUERY, {
    variables: STAFF_FILTERS,
    fetchPolicy: 'network-only',
    skip: !canAssignTask,
  });

  const staffUsers = useMemo(
    () => (canAssignTask ? staffData?.usersFilter?.users || [] : []),
    [canAssignTask, staffData?.usersFilter?.users],
  );

  const getStaffDisplayName = useCallback((user) => {
    const nameParts = [user?.firstName, user?.middleName, user?.lastName]
      .map((part) => (part || '').trim())
      .filter(Boolean);
    if (nameParts.length) return nameParts.join(' ');
    return user?.email || user?.phone || 'User';
  }, []);

  const getStaffRoleLabel = useCallback((user) => {
    const normalizeRole = (roleName = '') => roleName.replace(/_/g, ' ');
    const roleNames = (user?.roles || [])
      .map((role) => role?.name)
      .filter(Boolean)
      .map((role) => normalizeRole(role));
    const filteredRoles = roleNames.filter((role) => role !== 'TRADER');
    const rolesToShow = filteredRoles.length ? filteredRoles : roleNames;
    if (!rolesToShow.length) return 'ROLE';
    return rolesToShow.join(', ');
  }, []);

  const getStaffPhoneLabel = useCallback((user) => {
    const code = user?.callingCode?.callingCode || '';
    const phone = user?.phone || '';
    const number = `${code} ${phone}`.trim();
    return number || 'Phone N/A';
  }, []);

  const formatStaffOptionLabel = useCallback(
    (user) => {
      const name = getStaffDisplayName(user);
      const phone = getStaffPhoneLabel(user);
      const roles = getStaffRoleLabel(user);
      const id = user?.id ? `#${user.id}` : '';
      return `${name} | ${phone} | ${roles} ${id}`.trim();
    },
    [getStaffDisplayName, getStaffPhoneLabel, getStaffRoleLabel],
  );

  const staffSelectOptions = useMemo(
    () =>
      staffUsers.map((user) => ({
        value: `${user.id}`,
        label: formatStaffOptionLabel(user),
        user,
      })),
    [formatStaffOptionLabel, staffUsers],
  );

  const formatCommentTimestamp = (value) => {
    const date = parseTimestampToDate(value);
    if (!date) return '';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const extractFileNameFromUrl = (url = '') => {
    if (!url) return 'attachment';
    const cleanUrl = String(url).split('?')[0];
    const name = cleanUrl.split('/').filter(Boolean).pop();
    return decodeURIComponent(name || 'attachment');
  };

  const isImageUrl = (value = '') =>
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(String(value));

  const downloadFileFromUrl = async (url, fileName) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const name = fileName || extractFileNameFromUrl(url);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Unable to download attachment.');
    }
  };

  const getCurrentUserId = useCallback(
    () =>
      auth?.data?.userId ??
      auth?.data?.id ??
      auth?.data?.user_id ??
      auth?.data?.user?.id ??
      auth?.data?.user?.userId ??
      auth?.data?.staffId ??
      auth?.data?.staff?.id ??
      getAssignedToIdFromStorage() ??
      null,
    [auth?.data],
  );

  const getCurrentUserFullName = useCallback(() => {
    const firstName = auth?.data?.firstName?.trim?.() || '';
    const lastName = auth?.data?.lastName?.trim?.() || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || auth?.data?.email || 'Admin';
  }, [auth?.data?.email, auth?.data?.firstName, auth?.data?.lastName]);

  const refreshAuthPermissions = useCallback(async () => {
    const token = auth?.data?.token;
    const userId = getCurrentUserId();
    if (!token || !userId) return;
    const now = Date.now();
    if (authRefreshInFlightRef.current) return;
    if (now - lastAuthRefreshAtRef.current < 3000) return;
    authRefreshInFlightRef.current = true;
    lastAuthRefreshAtRef.current = now;
    try {
      const { data: userInfo } = await fetchUserInfo({
        variables: { id: userId },
      });
      const userData = userInfo?.user;
      if (!userData) return;

      let permissions = Array.isArray(userData?.permissions)
        ? userData.permissions
        : [];
      const roles = Array.isArray(userData?.roles) ? userData.roles : [];

      if (!permissions.length && roles.length) {
        const { data: rolesData } = await fetchRolesWithPermissions();
        const userRoleNames = new Set(
          roles.map((role) => role?.name).filter(Boolean),
        );
        const rolePermissions = [];
        (rolesData?.roles || []).forEach((role) => {
          if (!userRoleNames.has(role?.name)) return;
          (role?.permissions || []).forEach((permission) => {
            rolePermissions.push(permission);
          });
        });
        if (rolePermissions.length) {
          permissions = rolePermissions;
        }
      }

      const nextAuth = {
        ...auth?.data,
        ...userData,
        roles,
        permissions,
      };
      setAuthData(nextAuth);
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
    } finally {
      authRefreshInFlightRef.current = false;
    }
  }, [
    auth?.data,
    fetchRolesWithPermissions,
    fetchUserInfo,
    getCurrentUserId,
    setAuthData,
  ]);

  const notifyNewAssignments = useCallback((apiTasks = []) => {
    if (!Array.isArray(apiTasks)) return;
    const currentUserId = getAssignedToIdFromStorage();
    if (!currentUserId) return;

    const currentIds = new Set(
      apiTasks
        .map((task) => task?.id)
        .filter((taskId) => taskId !== null && taskId !== undefined),
    );

    if (!taskNotificationInitRef.current) {
      taskNotificationInitRef.current = true;
      knownTaskIdsRef.current = currentIds;
      return;
    }

    knownTaskIdsRef.current = currentIds;
  }, []);

  const fetchTasks = useCallback(
    async (options = {}) => {
      const { force = false, pageNo = currentPage, append = false } = options;
      const now = Date.now();
      if (!force && now - lastFetchAtRef.current < 800) return [];
      if (tasksFetchInFlight.current) {
        if (force) {
          pendingFetchRef.current = true;
        }
        return [];
      }
      lastFetchAtRef.current = now;
      tasksFetchInFlight.current = true;
      setTasksLoading(true);
      setTasksError('');
      const assignedToId = getAssignedToIdFromStorage();
      const currentUserCandidates = getCurrentUserIdentityCandidates(
        auth?.data,
      );
      const currentUserId = currentUserCandidates[0] || assignedToId;
      const selectedTab =
        taskOwnershipTabs.find((tab) => tab.key === ownershipTab) ||
        taskOwnershipTabs[0];
      let tasksEndpoint = `${process.env.REACT_APP_REST_API_ENDPOINT}tasks`;
      if (selectedTab?.endpoint === 'tasks/escalated') {
        tasksEndpoint = `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/escalated?pageNo=${pageNo}&pageSize=${pageSize}`;
      } else if (currentUserId) {
        tasksEndpoint = `${process.env.REACT_APP_REST_API_ENDPOINT}tasks?${selectedTab?.queryKey || 'assignedToId'}=${encodeURIComponent(
          currentUserId,
        )}`;
      }
      const response = await axiosGetAPI(tasksEndpoint, auth);
      if (response?.isSuccessful) {
        const responseTasks = getApiListData(response);
        let apiTasks = responseTasks;

        // Handle pagination data for escalated tasks
        if (selectedTab?.endpoint === 'tasks/escalated') {
          const paginationData = response?.data;
          if (paginationData) {
            setTotalElements(paginationData.totalElements || 0);
            setHasMore(!paginationData.last);
          }
        }

        if (ownershipTab === 'assigned') {
          notifyNewAssignments(apiTasks);
          const assignedTasks = responseTasks.filter((task) =>
            taskMatchesCurrentUser(task, auth?.data),
          );
          if (assignedTasks.length) {
            apiTasks = assignedTasks;
          } else {
            const fallbackResponse = await axiosGetAPI(
              `${process.env.REACT_APP_REST_API_ENDPOINT}tasks`,
              auth,
            );
            if (fallbackResponse?.isSuccessful) {
              const fallbackTasks = getApiListData(fallbackResponse);
              const fallbackAssignedTasks = fallbackTasks.filter((task) =>
                taskMatchesCurrentUser(task, auth?.data),
              );
              if (fallbackAssignedTasks.length) {
                apiTasks = fallbackAssignedTasks;
              }
            }
          }
        }
        const nextTasks = buildTasksByStatusFromApi(apiTasks);

        // If append is true, merge with existing tasks
        if (append && selectedTab?.endpoint === 'tasks/escalated') {
          setTasksByStatus((prev) => {
            const merged = { ...prev };
            Object.keys(nextTasks).forEach((key) => {
              merged[key] = [...(prev[key] || []), ...nextTasks[key]];
            });
            return applyCommentCountsToTasks(merged, commentCountCacheRef.current);
          });
        } else {
          setTasksByStatus(
            applyCommentCountsToTasks(nextTasks, commentCountCacheRef.current),
          );
        }

        setHasLoadedTasks(true);
        fetchCommentCountsForTasks(apiTasks);
        setTasksLoading(false);
        tasksFetchInFlight.current = false;
        if (pendingFetchRef.current) {
          pendingFetchRef.current = false;
          fetchTasks({ force: true });
        }
        return apiTasks;
      }
      setTasksError(response?.message || 'Unable to load tasks');
      setHasLoadedTasks(true);
      setTasksLoading(false);
      tasksFetchInFlight.current = false;
      if (pendingFetchRef.current) {
        pendingFetchRef.current = false;
        fetchTasks({ force: true });
      }
      return [];
    },
    [
      auth,
      fetchCommentCountsForTasks,
      notifyNewAssignments,
      ownershipTab,
      taskOwnershipTabs,
      currentPage,
      pageSize,
    ],
  );

  useEffect(() => {
    if (!auth?.data?.token) return;
    let cancelled = false;
    const runRefresh = async () => {
      if (cancelled) return;
      await refreshAuthPermissions();
    };
    runRefresh();
    const interval = setInterval(runRefresh, 60000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') runRefresh();
    };
    window.addEventListener('focus', runRefresh);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', runRefresh);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [auth?.data?.token, refreshAuthPermissions]);

  useEffect(() => {
    if (!auth?.data?.token) return;
    if (lastAuthTokenRef.current === auth?.data?.token) return;
    lastAuthTokenRef.current = auth?.data?.token;
    fetchTasks();
  }, [auth?.data?.token, fetchTasks]);

  useEffect(() => {
    if (!auth?.data?.token) return;
    if (lastOwnershipTabRef.current === ownershipTab) return;
    lastOwnershipTabRef.current = ownershipTab;
    setCurrentPage(0); // Reset pagination when tab changes
    setTasksByStatus(EMPTY_TASKS); // Clear existing tasks
    fetchTasks({ force: true });
  }, [auth?.data?.token, fetchTasks, ownershipTab]);

  useEffect(() => {
    tasksByStatusRef.current = tasksByStatus;
  }, [tasksByStatus]);

  useEffect(() => {
    setAssigneeFilter('all');
  }, [ownershipTab]);

  useEffect(() => {
    if (canAssignTask) return;
    setTaskForm((prev) => {
      if (!prev.assignUser && !prev.assignee) return prev;
      return { ...prev, assignUser: false, assignee: '' };
    });
  }, [canAssignTask]);

  const toggleAddTask = () => {
    if (!canCreateTask) {
      toast.error('You do not have permission to create tasks.');
      return;
    }
    setIsAddTaskOpen((prev) => !prev);
  };

  const handleDragStart = (dragEvent, columnKey, taskId) => {
    const { dataTransfer } = dragEvent || {};
    if (dataTransfer) {
      dataTransfer.setData('text/plain', `${taskId}`);
      dataTransfer.effectAllowed = 'move';
    }
    setDraggedTask({ columnKey, taskId });
  };

  // FIX 3: Declared handleStatusChange BEFORE handleDrop so it's defined before use
  const handleStatusChange = async (
    taskId,
    currentColumn,
    nextStatusLabel,
    options = {},
  ) => {
    const { showUndo = true } = options;
    const targetColumn = STATUS_TO_KEY[nextStatusLabel] || currentColumn;
    if (targetColumn === currentColumn) return;

    const sourceTasks = tasksByStatusRef.current[currentColumn] || [];
    const taskToMove = sourceTasks.find((task) => task.id === taskId);
    const numericId = taskToMove?.sourceId;

    if (!numericId) {
      toast.error('Please refresh to update this task.');
      return;
    }

    const apiStatus = STATUS_API_VALUES[targetColumn] || nextStatusLabel;
    const response = await axiosUpdate(
      `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/${numericId}/status?status=${encodeURIComponent(
        apiStatus,
      )}`,
      {},
      auth?.data?.token,
    );

    if (response?.isSuccessful && response?.data?.error) {
      toast.error(response?.data?.message || 'Invalid status transition.');
      return;
    }
    if (!response?.isSuccessful) {
      toast.error(response?.message || 'Unable to update status.');
      return;
    }

    setTasksByStatus((prev) => {
      const updatedSource = [...(prev[currentColumn] || [])];
      const targetTasks = [...(prev[targetColumn] || [])];
      const taskIndex = updatedSource.findIndex((task) => task.id === taskId);
      if (taskIndex === -1) return prev;

      const [movedTask] = updatedSource.splice(taskIndex, 1);
      targetTasks.unshift(movedTask);

      return {
        ...prev,
        [currentColumn]: updatedSource,
        [targetColumn]: targetTasks,
      };
    });

    if (response?.data?.data === 'UPDATED') {
      if (showUndo) {
        toast.success(
          <div className="d-flex align-items-center gap-2">
            <span>Status updated.</span>
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                handleStatusChange(
                  taskId,
                  targetColumn,
                  STATUS_LABELS[currentColumn],
                  { showUndo: false },
                );
              }}
            >
              Undo
            </button>
          </div>,
          { autoClose: 5000 },
        );
      } else {
        toast.info('Status reverted.');
      }
    }
  };

  const handleDrop = (targetColumn) => {
    if (!draggedTask) return;
    if (draggedTask.columnKey === targetColumn) return;
    handleStatusChange(
      draggedTask.taskId,
      draggedTask.columnKey,
      STATUS_LABELS[targetColumn],
    );
    setDraggedTask(null);
    setActiveColumn('');
  };

  const handleTaskClick = useCallback((task, statusKey) => {
    const resolvedStatus =
      statusKey || task?.statusKey || normalizeStatusKey(task?.status);
    setSelectedTask({ ...task, statusKey: resolvedStatus });
    setIsTaskDetailOpen(true);
  }, []);

  const initialTaskOpenedRef = useRef(false);
  useEffect(() => {
    if (
      !detailOnly ||
      !initialTaskId ||
      !hasLoadedTasks ||
      initialTaskOpenedRef.current
    ) {
      return;
    }

    const matchingTask = Object.entries(tasksByStatus).reduce(
      (match, [statusKey, statusTasks]) => {
        if (match) return match;
        const task = statusTasks.find(
          (candidate) =>
            `${candidate?.sourceId ?? candidate?.id}` === `${initialTaskId}`,
        );
        return task ? { task, statusKey } : null;
      },
      null,
    );

    initialTaskOpenedRef.current = true;
    if (matchingTask) {
      handleTaskClick(matchingTask.task, matchingTask.statusKey);
    }
  }, [
    detailOnly,
    handleTaskClick,
    hasLoadedTasks,
    initialTaskId,
    tasksByStatus,
  ]);

  const closeTaskDetail = () => {
    setIsTaskDetailOpen(false);
    setSelectedTask(null);
    setComments([]);
    setCommentsLoading(false);
    setCommentsError('');
    setCommentInput('');
    commentDraftRef.current = '';
    lastCommentTaskIdRef.current = null;
    setCommentSubmitting(false);
    setDeletingCommentId(null);
    clearCommentAttachment();
    setCommentUploadLoading(false);
    setCommentImagePreviewUrl('');
    setIsCommentImagePreviewOpen(false);
    if (detailOnly) {
      onInitialTaskClose?.();
    }
  };

  const closeTaskDetailDrawer = () => {
    setIsTaskDetailOpen(false);
  };

  const updateTaskForm = (key, value) => {
    setTaskForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleTaskFormFlag = (key, resetKey) => {
    setTaskForm((prev) => {
      const nextValue = !prev[key];
      return {
        ...prev,
        [key]: nextValue,
        ...(nextValue ? {} : { [resetKey]: '' }),
      };
    });
  };

  const toggleDueDate = () => {
    setTaskForm((prev) => {
      const nextValue = !prev.setDueDate;
      return {
        ...prev,
        setDueDate: nextValue,
        ...(nextValue
          ? {}
          : {
            dueDate: '',
            repeatEnabled: false,
            repeatInterval: '',
            repeatEvery: '',
          }),
      };
    });
  };

  const toggleRepeat = () => {
    setTaskForm((prev) => {
      const nextValue = !prev.repeatEnabled;
      return {
        ...prev,
        repeatEnabled: nextValue,
        ...(nextValue
          ? { repeatInterval: '1' }
          : { repeatInterval: '', repeatEvery: '' }),
      };
    });
  };

  const openCommentImagePreview = (url) => {
    if (!url) return;
    closeTaskDetailDrawer();
    setCommentImagePreviewUrl(url);
    setIsCommentImagePreviewOpen(true);
  };

  const closeCommentImagePreview = () => {
    setIsCommentImagePreviewOpen(false);
    setCommentImagePreviewUrl('');
    if (detailOnly) {
      setIsTaskDetailOpen(true);
    }
  };

  const resetTaskForm = () => {
    setTaskForm({
      title: '',
      details: '',
      setDueDate: false,
      dueDate: '',
      repeatEnabled: false,
      repeatInterval: '',
      repeatEvery: '',
      assignUser: false,
      assignee: '',
    });
  };

  const handleSaveTask = async () => {
    if (isSavingTask) return;
    if (!canCreateTask) {
      toast.error('You do not have permission to create tasks.');
      return;
    }
    const trimmedTitle = taskForm.title.trim();
    if (!trimmedTitle) return;
    if (taskForm.assignUser && !taskForm.assignee) {
      toast.error('Please select a user.');
      return;
    }
    if (taskForm.setDueDate && !taskForm.dueDate) {
      toast.error('Please select a due date.');
      return;
    }

    const selectedUser = staffUsers.find(
      (user) => `${user?.id}` === `${taskForm.assignee}`,
    );
    const resolvedAssignee =
      taskForm.assignUser && selectedUser
        ? getStaffDisplayName(selectedUser)
        : '';

    const assignedTo =
      taskForm.assignUser && canAssignTask ? Number(taskForm.assignee) : null;
    const assignedToId = Number.isFinite(assignedTo) ? assignedTo : null;
    const createdByValue = Number(getCurrentUserId());

    const payload = {
      title: trimmedTitle,
      description: taskForm.details.trim(),
      repeatInterval: taskForm.repeatEnabled ? taskForm.repeatInterval : '',
      repeatDuration: taskForm.repeatEnabled
        ? taskForm.repeatEvery || null
        : null,
    };
    if (taskForm.setDueDate) {
      payload.dueDate = new Date(taskForm.dueDate).toISOString();
    }
    if (assignedToId !== null) {
      payload.assignedToId = assignedToId;
    }
    if (Number.isFinite(createdByValue)) {
      payload.createdBy = createdByValue;
    }

    setIsSavingTask(true);
    const response = await axiosPost(
      `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/manual`,
      payload,
      {},
      auth,
    );

    if (!response?.isSuccessful || response?.data?.data !== 'CREATED') {
      toast.error(response?.message || 'Unable to create task.');
      setIsSavingTask(false);
      return;
    }

    let createdTaskId =
      response?.data?.task?.id ??
      response?.data?.data?.id ??
      response?.data?.id ??
      null;

    const notificationText = resolvedAssignee
      ? `Task assigned to ${resolvedAssignee}: ${trimmedTitle}`
      : `Task created: ${trimmedTitle}`;

    if (
      assignedToId !== null &&
      selectedUser &&
      `${assignedToId}` !== `${createdByValue}`
    ) {
      try {
        const latestTasks = await fetchTasks({ force: true });
        if (!createdTaskId) {
          const matchedTask = findCreatedTaskMatch({
            apiTasks: latestTasks,
            createdByValue,
            assignedToId,
            title: trimmedTitle,
            description: taskForm.details.trim(),
            dueDate: taskForm.setDueDate ? taskForm.dueDate : '',
          });
          createdTaskId = matchedTask?.id ?? null;
        }

        const createdAtMs = Date.now();
        const taskNotificationRef = doc(
          collection(db, 'task_notifications'),
          buildTaskNotificationDocId({
            recipientUserId: assignedToId,
            taskId: createdTaskId,
          }),
        );

        await setDoc(taskNotificationRef, {
          type: 'TASK_ASSIGNED',
          recipientUserId: `${assignedToId}`,
          recipientName: resolvedAssignee,
          senderUserId: Number.isFinite(createdByValue)
            ? `${createdByValue}`
            : '',
          senderName: getCurrentUserFullName(),
          taskId: createdTaskId ? `${createdTaskId}` : '',
          taskIdNumber: createdTaskId ? Number(createdTaskId) : null,
          clickAction: '/task',
          taskTitle: trimmedTitle,
          taskDescription: taskForm.details.trim(),
          title: trimmedTitle,
          description: taskForm.details.trim(),
          message:
            taskForm.details.trim() || 'A new task has been assigned to you.',
          isRead: false,
          createdAtMs,
          deliveryWindowMs: TASK_NOTIFICATION_DELIVERY_WINDOW_MS,
          taskDeleted: false,
          createdAt: serverTimestamp(),
        });
      } catch (firebaseError) {
        console.error('Failed to create task notification:', firebaseError);
      }
    } else {
      await fetchTasks({ force: true });
    }

    toast.success(notificationText);
    resetTaskForm();
    setIsAddTaskOpen(false);
    setIsSavingTask(false);
  };

  const openDeleteTaskConfirmation = useCallback((taskId, currentColumn) => {
    setDeleteTaskTarget({ taskId, currentColumn });
    setShowDeleteRecord(true);
  }, []);

  const closeDeleteTaskConfirmation = useCallback(() => {
    if (isDeletingTask) return;

    setDeleteTaskTarget(null);
    setShowDeleteRecord(false);
  }, [isDeletingTask]);

  const handleDeleteTask = async () => {
    const taskId = deleteTaskTarget?.taskId;
    const currentColumn = deleteTaskTarget?.currentColumn;

    if (!taskId || !currentColumn) return;

    const sourceTasks = tasksByStatus[currentColumn] || [];
    const taskToDelete = sourceTasks.find((task) => task.id === taskId);
    const numericId = taskToDelete?.sourceId;
    const assignedToId = taskToDelete?.assignedToId;
    const normalizedTaskTitle = normalizeTextForMatch(taskToDelete?.title);
    const normalizedTaskDescription = normalizeTextForMatch(
      taskToDelete?.description,
    );

    if (!numericId) {
      toast.error('Please refresh to delete this task.');
      return;
    }

    const userId = getCurrentUserId();
    const deleteUrl = userId
      ? `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/${numericId}?userId=${userId}`
      : `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/${numericId}`;

    setIsDeletingTask(true);
    try {
      const response = await axiosDelete(deleteUrl, auth?.data?.token);
      if (response?.isSuccessful && response?.data?.data === 'DELETED') {
        try {
          const taskNotificationCollection = collection(
            db,
            'task_notifications',
          );
          const taskIdValue = `${numericId}`;
          const safeTaskId = `${numericId}`.replace(/[^a-zA-Z0-9_-]/g, '');
          const notificationDocId = buildTaskNotificationDocId({
            recipientUserId: assignedToId,
            taskId: numericId,
          });
          const notificationSnapshots = await Promise.all([
            getDocs(
              query(
                taskNotificationCollection,
                where('taskId', '==', taskIdValue),
              ),
            ),
            getDocs(
              query(
                taskNotificationCollection,
                where('taskIdNumber', '==', Number(numericId)),
              ),
            ),
            assignedToId !== null && assignedToId !== undefined
              ? getDocs(
                query(
                  taskNotificationCollection,
                  where('recipientUserId', '==', `${assignedToId}`),
                  limit(100),
                ),
              )
              : Promise.resolve({ forEach: () => { } }),
          ]);

          const notificationDocs = new Map();

          try {
            const directNotificationRef = doc(
              db,
              'task_notifications',
              notificationDocId,
            );
            notificationDocs.set(notificationDocId, directNotificationRef);
          } catch (error) {
            console.error(
              'Failed to prepare direct task notification reference:',
              error,
            );
          }

          notificationSnapshots.forEach((snapshot) => {
            snapshot.forEach((docSnapshot) => {
              const notificationData = docSnapshot.data() || {};
              const matchesLegacyId = safeTaskId
                ? `${docSnapshot.id}`.includes(safeTaskId)
                : false;
              const matchesTaskId =
                `${notificationData?.taskId || ''}` === taskIdValue;
              const matchesTaskIdNumber =
                Number(notificationData?.taskIdNumber) === Number(numericId);
              const matchesLegacyContent =
                !notificationData?.taskId &&
                !notificationData?.taskIdNumber &&
                normalizeTextForMatch(
                  notificationData?.taskTitle || notificationData?.title,
                ) === normalizedTaskTitle &&
                normalizeTextForMatch(
                  notificationData?.taskDescription ||
                  notificationData?.description,
                ) === normalizedTaskDescription;

              if (
                !matchesLegacyId &&
                !matchesTaskId &&
                !matchesTaskIdNumber &&
                !matchesLegacyContent
              ) {
                return;
              }

              notificationDocs.set(docSnapshot.id, docSnapshot.ref);
            });
          });

          if (notificationDocs.size === 1) {
            const [notificationRef] = notificationDocs.values();
            await deleteDoc(notificationRef);
          } else if (notificationDocs.size > 1) {
            const batch = writeBatch(db);
            notificationDocs.forEach((notificationRef) => {
              batch.delete(notificationRef);
            });
            await batch.commit();
          }
        } catch (firebaseError) {
          console.error(
            'Failed to delete task notifications from Firebase:',
            firebaseError,
          );
        }

        setTasksByStatus((prev) => ({
          ...prev,
          [currentColumn]: (prev[currentColumn] || []).filter(
            (task) => task.id !== taskId,
          ),
        }));
        if (`${selectedTask?.sourceId}` === `${numericId}`) {
          setSelectedTask(null);
          setIsTaskDetailOpen(false);
          setComments([]);
        }
        toast.success('Task deleted.');
        setDeleteTaskTarget(null);
        setShowDeleteRecord(false);
        return;
      }
      const errorMessage =
        response?.message ||
        response?.data?.message ||
        response?.data?.error ||
        'Unable to delete task.';
      toast.error(errorMessage);
    } finally {
      setIsDeletingTask(false);
    }
  };

  const handleMarkTaskAsRead = async (task) => {
    const taskId = `${task?.sourceId ?? task?.id ?? ''}`.trim();
    if (!taskId) {
      toast.error('Task id is missing.');
      return;
    }

    if (markingTaskId === taskId) return;

    setMarkingTaskId(taskId);
    try {
      const response = await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/notify/${taskId}`,
        {},
        {},
        auth,
      );

      if (response?.isSuccessful) {
        const message =
          response?.data?.message ||
          response?.message ||
          'Notification marked as read and task completed successfully';
        toast.success(message);
        setMarkedAsReadTaskIds((prev) => ({
          ...prev,
          [taskId]: true,
        }));
        await fetchTasks({ force: true });
      } else {
        toast.error(
          response?.data?.message ||
          response?.message ||
          'Unable to mark notification as read.',
        );
      }
    } catch (error) {
      console.error('Failed to mark task as read:', error);
      toast.error(
        error?.message || 'Unable to mark notification as read.',
      );
    } finally {
      setMarkingTaskId(null);
    }
  };

  const isSameDay = (left, right) =>
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();

  const toStartOfDay = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const matchesDateFilter = (dateValue, filter) => {
    if (filter === 'all') return true;
    if (!dateValue) return false;
    const valueDate = new Date(dateValue);
    if (Number.isNaN(valueDate.getTime())) return false;
    const now = new Date();

    if (filter === 'today') {
      return isSameDay(valueDate, now);
    }

    if (filter === 'thisWeek') {
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      return valueDate >= startOfWeek && valueDate <= endOfWeek;
    }

    if (filter === 'thisMonth') {
      return (
        valueDate.getFullYear() === now.getFullYear() &&
        valueDate.getMonth() === now.getMonth()
      );
    }

    return true;
  };

  const assigneeOptions = useMemo(() => {
    if (ownershipTab !== 'created') return [];
    const names = new Set();
    Object.values(tasksByStatus)
      .flat()
      .forEach((task) => {
        const rawName = (task?.assignee || '').trim();
        const name = rawName || 'Unassigned';
        names.add(name);
      });
    const sorted = Array.from(names).sort((a, b) => a.localeCompare(b));
    return [
      { label: 'All', value: 'all' },
      ...sorted.map((name) => ({ label: name, value: name })),
    ];
  }, [ownershipTab, tasksByStatus]);

  const ownershipTabLabel = useMemo(() => {
    const match = taskOwnershipTabs.find((tab) => tab.key === ownershipTab);
    return match?.label || taskOwnershipTabs[0].label;
  }, [ownershipTab, taskOwnershipTabs]);

  useEffect(() => {
    if (!taskOwnershipTabs.length) return;
    const hasSelectedTab = taskOwnershipTabs.some(
      (tab) => tab.key === ownershipTab,
    );
    if (!hasSelectedTab) {
      setOwnershipTab(taskOwnershipTabs[0].key);
    }
  }, [ownershipTab, taskOwnershipTabs]);

  useEffect(() => {
    if (ownershipTab !== 'created') return;
    if (!assigneeOptions.length) return;
    const hasMatch = assigneeOptions.some(
      (option) => option.value.toLowerCase() === assigneeFilter.toLowerCase(),
    );
    if (!hasMatch) setAssigneeFilter('all');
  }, [assigneeFilter, assigneeOptions, ownershipTab]);

  const selectedAssigneeOption = useMemo(
    () =>
      staffSelectOptions.find(
        (option) => `${option.value}` === `${taskForm.assignee}`,
      ) || null,
    [staffSelectOptions, taskForm.assignee],
  );

  const selectedAssigneeFilterOption = useMemo(
    () =>
      assigneeOptions.find(
        (option) => `${option.value}`.toLowerCase() === assigneeFilter.toLowerCase(),
      ) || assigneeOptions[0] || null,
    [assigneeFilter, assigneeOptions],
  );

  const matchesFilters = (task) => {
    const categoryMatch =
      filters.category === 'all' || task.category === filters.category;
    const createdMatch = matchesDateFilter(task.createdAt, filters.created);
    const dueMatch = matchesDateFilter(task.dueDate, filters.due);
    const assigneeMatch =
      ownershipTab !== 'created' ||
      assigneeFilter === 'all' ||
      `${task.assignee || 'Unassigned'}`.toLowerCase() ===
      assigneeFilter.toLowerCase();
    return categoryMatch && createdMatch && dueMatch && assigneeMatch;
  };

  const filteredTasksByStatus = TASK_COLUMNS.reduce((acc, column) => {
    acc[column.key] = (tasksByStatus[column.key] || []).filter(matchesFilters);
    return acc;
  }, {});

  const visibleColumns =
    filters.status === 'all'
      ? TASK_COLUMNS
      : TASK_COLUMNS.filter((column) => column.key === filters.status);

  const listColumns =
    filters.status === 'all'
      ? TASK_COLUMNS.map((column) => column.key)
      : [filters.status];

  const listTasks = listColumns.flatMap((columnKey) =>
    (filteredTasksByStatus[columnKey] || []).map((task) => ({
      task,
      status: columnKey,
    })),
  );

  const todayStart = toStartOfDay(new Date());
  const dayMs = 24 * 60 * 60 * 1000;
  const getDueBucket = (task) => {
    const dueDate = toStartOfDay(task.dueDate);
    if (!dueDate || !todayStart) return 'upcoming';
    const diffDays = Math.floor((dueDate - todayStart) / dayMs);
    if (diffDays < 0) return 'overdue';
    if (diffDays <= 15) return 'today';
    return 'upcoming';
  };

  const listSections = [
    { key: 'overdue', title: 'Overdue', tone: 'overdue' },
    {
      key: 'today',
      title: ownershipTab === 'escalated' && totalElements > 0
        ? `Today's (Total: ${totalElements} tasks)`
        : "Today's",
      tone: 'today'
    },
    { key: 'upcoming', title: 'Upcoming', tone: 'upcoming' },
  ];

  const listTasksByBucket = listSections.reduce((acc, section) => {
    acc[section.key] = [];
    return acc;
  }, {});

  listTasks.forEach(({ task, status }) => {
    const bucket = getDueBucket(task);
    listTasksByBucket[bucket].push({ task, status });
  });

  const listVisibleSections = listSections.filter(
    (section) => (listTasksByBucket[section.key] || []).length > 0,
  );

  const truncateWords = (text = '', maxWords = 4) => {
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return `${words.slice(0, maxWords).join(' ')}...`;
  };

  const fetchComments = useCallback(
    async (taskId) => {
      if (!taskId) return;
      setComments([]);
      setCommentsLoading(true);
      setCommentsError('');
      const response = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/${taskId}/comments`,
        auth,
      );
      if (response?.isSuccessful) {
        const apiComments = response?.data?.data || [];
        const normalized = (apiComments || []).map((comment) => ({
          id: comment?.id,
          comment: comment?.comment || '',
          commentedById: comment?.commentedById,
          commentedByName: comment?.commentedByName || 'User',
          imageUrl: comment?.imageUrl || '',
          createdAt: comment?.createdAt,
        }));
        normalized.sort((left, right) => {
          const leftDate = parseTimestampToDate(left.createdAt);
          const rightDate = parseTimestampToDate(right.createdAt);
          return (rightDate?.getTime() || 0) - (leftDate?.getTime() || 0);
        });
        setComments(normalized);
        updateTaskCommentCount(taskId, normalized.length);
      } else {
        setComments([]);
        setCommentsError(response?.message || 'Unable to load comments.');
      }
      setCommentsLoading(false);
    },
    [auth, updateTaskCommentCount],
  );

  useEffect(() => {
    fetchCommentsRef.current = fetchComments;
  }, [fetchComments]);

  useEffect(() => {
    return () => {
      if (commentPreviewUrl && commentPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(commentPreviewUrl);
      }
    };
  }, [commentPreviewUrl]);

  const handleCommentFileChange = async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    commentDraftRef.current = commentInput;
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size should not exceed 5 MB.');
      if (commentFileInputRef.current) {
        commentFileInputRef.current.value = '';
      }
      return;
    }
    setCommentAttachment(file);
    if (file.type?.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      setCommentPreviewUrl(previewUrl);
    } else {
      setCommentPreviewUrl('');
    }
    setCommentUploadLoading(true);
    setCommentUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}common/upload-file?containerName=assets`,
        formData,
        {},
        auth,
      );
      const uploadedUrl = response?.data?.data;
      if (response?.isSuccessful && uploadedUrl) {
        setCommentAttachmentUrl(uploadedUrl);
      } else {
        const message = response?.message || 'Unable to upload file.';
        setCommentUploadError(message);
        setCommentAttachment(null);
        setCommentAttachmentUrl('');
        setCommentPreviewUrl('');
        toast.error(message);
      }
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      setCommentUploadError('Unable to upload file.');
      setCommentAttachment(null);
      setCommentAttachmentUrl('');
      setCommentPreviewUrl('');
      toast.error('Unable to upload file.');
    } finally {
      setCommentUploadLoading(false);
      setCommentInput((prev) => prev || commentDraftRef.current);
    }
  };

  const handleAddComment = async () => {
    if (commentSubmitting) return;
    const trimmedComment = commentInput.trim();
    if (commentUploadLoading) {
      toast.info('Please wait for the attachment to finish uploading.');
      return;
    }
    if (!trimmedComment) {
      toast.error('Please enter a comment.');
      return;
    }
    if (commentAttachment && !commentAttachmentUrl) {
      toast.error('Attachment upload failed. Please try again.');
      return;
    }
    const taskId = selectedTask?.sourceId;
    if (!taskId) {
      toast.error('Task id is missing.');
      return;
    }
    const userId = getCurrentUserId();
    if (!userId) {
      toast.error('User id is missing.');
      return;
    }
    setCommentSubmitting(true);
    const payload = {
      comment: trimmedComment,
      ...(commentAttachmentUrl ? { imageUrl: commentAttachmentUrl } : {}),
    };
    const response = await axiosPost(
      `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/${taskId}/comments?userId=${userId}`,
      payload,
      {},
      auth,
    );
    if (response?.isSuccessful && response?.data?.data === 'CREATED') {
      setCommentInput('');
      commentDraftRef.current = '';
      clearCommentAttachment();
      await fetchComments(taskId);
      toast.success('Comment added.');
    } else {
      toast.error(response?.message || 'Unable to add comment.');
    }
    setCommentSubmitting(false);
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentId) return;
    const taskId = selectedTask?.sourceId;
    if (!taskId) {
      toast.error('Task id is missing.');
      return;
    }
    const userId = getCurrentUserId();
    if (!userId) {
      toast.error('User id is missing.');
      return;
    }
    setDeletingCommentId(commentId);
    const response = await axiosDelete(
      `${process.env.REACT_APP_REST_API_ENDPOINT}tasks/${taskId}/comments/${commentId}?userId=${userId}`,
      auth?.data?.token,
    );
    if (response?.isSuccessful && response?.data?.data === 'DELETED') {
      setComments((prev) => {
        const next = prev.filter((comment) => comment.id !== commentId);
        updateTaskCommentCount(taskId, next.length);
        return next;
      });
      toast.success('Comment deleted.');
    } else {
      toast.error(response?.message || 'Unable to delete comment.');
    }
    setDeletingCommentId(null);
  };

  useEffect(() => {
    if (!isTaskDetailOpen) return;
    const taskId = selectedTask?.sourceId;
    if (!taskId) {
      setComments([]);
      setCommentsError('Unable to load comments for this task.');
      return;
    }
    const isNewTask = `${lastCommentTaskIdRef.current || ''}` !== `${taskId}`;
    if (isNewTask) {
      setCommentInput('');
      commentDraftRef.current = '';
      clearCommentAttachment();
      lastCommentTaskIdRef.current = taskId;
    }
    if (fetchCommentsRef.current) {
      fetchCommentsRef.current(taskId);
    }
  }, [clearCommentAttachment, isTaskDetailOpen, selectedTask?.sourceId]);

  // FIX 4: Extracted board/list rendering into separate variables to avoid nested ternary
  const renderKanbanBoard = () => (
    <div className="task-board-wrap">
      <div className="task-board">
        {visibleColumns.map((column) => {
          const tasks = filteredTasksByStatus[column.key] || [];
          return (
            <div
              key={column.key}
              className={`task-column ${column.tone} ${activeColumn === column.key ? 'drag-over' : ''
                }`}
              onDragOver={(dragEvent) => dragEvent.preventDefault()}
              onDragEnter={() => setActiveColumn(column.key)}
              onDragLeave={() => setActiveColumn('')}
              onDrop={() => handleDrop(column.key)}
            >
              <div className="task-column-header">
                <div className="task-column-title">
                  <span className="task-column-dot" />
                  {column.title}
                </div>
                <span className="task-count">{tasks.length}</span>
              </div>
              <div className="task-column-body">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className="task-card"
                    role="button"
                    tabIndex={0}
                    draggable
                    onDragStart={(dragEvent) =>
                      handleDragStart(dragEvent, column.key, task.id)
                    }
                    onDragEnd={() => {
                      setDraggedTask(null);
                      setActiveColumn('');
                    }}
                    onClick={() => handleTaskClick(task, column.key)}
                    onKeyDown={(keyEvent) => {
                      if (keyEvent.key === 'Enter')
                        handleTaskClick(task, column.key);
                    }}
                  >
                    <div className="task-card-top">
                      <h4 className="task-title mb-0">{task.title}</h4>
                      <span
                        className={`task-priority ${task.priority.toLowerCase()}`}
                      >
                        {task.priority}
                      </span>
                    </div>
                    <p className="task-desc">{task.description}</p>
                    <div className="task-meta">
                      <div className="task-meta-left">
                        <span className="task-role">{task.assignee}</span>
                        {task.commentCount > 0 ? (
                          <span
                            className="task-comment-flag"
                            title={`${task.commentCount} comment${task.commentCount === 1 ? '' : 's'
                              }`}
                          >
                            <svg
                              className="task-comment-icon"
                              viewBox="0 0 20 20"
                              aria-hidden="true"
                            >
                              <path d="M5 4.5h10a2.5 2.5 0 0 1 2.5 2.5v6a2.5 2.5 0 0 1-2.5 2.5H9.2l-3.6 3.2c-.4.4-1 .1-1-.4v-2.8A2.5 2.5 0 0 1 2 13V7a2.5 2.5 0 0 1 3-2.5z" />
                            </svg>
                            <span>Comments {task.commentCount}</span>
                          </span>
                        ) : null}
                      </div>
                      <span className="task-deadline">
                        Due: {task.dueDate || '-'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderListView = () => (
    <div className="task-list">
      {listVisibleSections.length ? (
        <>
          {listVisibleSections.map((section) => {
            const sectionTasks = listTasksByBucket[section.key] || [];
            return (
              <div
                key={section.key}
                className={`task-list-section ${section.tone}`}
              >
                <div className="task-list-header">
                  <span className="task-list-section-title">{section.title}</span>
                  <span className="task-section-count">
                    {sectionTasks.length} {sectionTasks.length === 1 ? 'TASK' : 'TASKS'}
                  </span>
                </div>
                <div className="task-list-body">
                  {sectionTasks.map(({ task, status }) => {
                    const isDone = status === 'completed';
                    const isExpanded = expandedTaskId === task.id;
                    const statusLabel =
                      STATUS_LABELS[status] || STATUS_LABELS.pending;
                    const taskIdValue = `${task?.sourceId ?? task?.id ?? ''}`.trim();
                    const isTaskMarkedAsRead =
                      isDone || Boolean(markedAsReadTaskIds[taskIdValue]);
                    const ertCountdown = getTaskCountdownLabel(
                      task,
                      currentTimeMs,
                      'ert',
                    );
                    const reminderCountdown = getTaskReminderCountdownLabel(
                      task,
                      currentTimeMs,
                    );
                    return (
                      <div
                        key={task.id}
                        className={`task-list-row ${isDone ? 'completed' : ''} ${isExpanded ? 'expanded' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleTaskClick(task, status)}
                        onKeyDown={(keyEvent) => {
                          if (keyEvent.key === 'Enter')
                            handleTaskClick(task, status);
                        }}
                      >
                        <div className="task-list-main">
                          <span
                            className={`task-list-accent ${task.priority.toLowerCase()}`}
                          />
                          <div className="task-list-text">
                            <div className="task-list-title-row">
                              <div className="task-list-title">
                                {truncateWords(task.title, 8)}
                              </div>
                              <div className="task-list-select">
                                <select
                                  className={`task-list-select-control ${statusLabel.toLowerCase()}`}
                                  value={statusLabel}
                                  onChange={(changeEvent) =>
                                    handleStatusChange(
                                      task.id,
                                      status,
                                      changeEvent.target.value,
                                    )
                                  }
                                  onClick={(clickEvent) => clickEvent.stopPropagation()}
                                >
                                  {Object.values(STATUS_LABELS).map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {task.commentCount > 0 ? (
                                <span
                                  className="task-list-comment-badge"
                                  title={`${task.commentCount} comment${task.commentCount === 1 ? '' : 's'
                                    }`}
                                  aria-label={`${task.commentCount} comment${task.commentCount === 1 ? '' : 's'
                                    }`}
                                >
                                  {task.commentCount}
                                </span>
                              ) : null}
                            </div>
                            {task.description ? (
                              <div className="task-list-desc">
                                {truncateWords(task.description, 5)}
                              </div>
                            ) : (
                              <div className="task-list-desc task-list-desc-empty">
                                No description
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          className={`task-mobile-expand ${isExpanded ? 'is-open' : ''}`}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? 'Collapse task details' : 'Expand task details'}
                          onClick={(clickEvent) => {
                            clickEvent.stopPropagation();
                            setExpandedTaskId((previousId) => (
                              previousId === task.id ? null : task.id
                            ));
                          }}
                          onKeyDown={(keyEvent) => keyEvent.stopPropagation()}
                        >
                          <RiArrowDownSLine aria-hidden="true" size={20} />
                        </button>

                        <div className="task-list-date task-list-ert">
                          <span className="task-list-date-label">ERT</span>
                          <span
                            className={`task-list-date-value task-list-pill-value ${ertCountdown === 'Expired'
                              ? 'task-list-pill-expired'
                              : ''
                              }`}
                          >
                            <RiTimeLine className="me-1" size={14} />
                            {ertCountdown}
                          </span>
                        </div>

                        <div className="task-list-date task-list-reminder">
                          <span className="task-list-date-label">Reminder</span>
                          <span
                            className={`task-list-date-value task-list-pill-value ${reminderCountdown === 'Expired'
                              ? 'task-list-pill-expired'
                              : ''
                              }`}
                          >
                            <RiNotification3Line className="me-1" size={14} />
                            {reminderCountdown}
                          </span>
                        </div>

                        <div className="task-list-date task-list-escalation">
                          <span className="task-list-date-label">
                            {ownershipTab === 'escalated'
                              ? 'Escalation From'
                              : 'Escalation To'}
                          </span>
                          <div className="task-list-date-value task-list-escalation-value">
                            {task.assignee && task.assignee !== 'Unassigned' ? (
                              <>
                                <span className="task-list-avatar">
                                  {task.assignee.substring(0, 2).toUpperCase()}
                                </span>
                                <span className="task-list-escalation-name">{task.assignee}</span>
                              </>
                            ) : (
                              '-'
                            )}
                          </div>
                        </div>

                        <div className="task-list-date task-list-due">
                          <span className="task-list-date-label">Due Date</span>
                          <span className="task-list-date-value">
                            {task.dueDate || '-'}
                          </span>
                        </div>

                        <div className="task-list-actions-group">
                          <button
                            type="button"
                            className="btn btn-sm btn-dark task-list-action-btn"
                            disabled={
                              isTaskMarkedAsRead ||
                              markingTaskId === taskIdValue
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkTaskAsRead(task);
                            }}
                          >
                            {isTaskMarkedAsRead ? 'Mark as read' : 'Notify'}
                          </button>
                          <div className="task-list-actions">
                            <UncontrolledDropdown>
                              <DropdownToggle
                                className="task-list-menu"
                                color="link"
                                caret={false}
                                onClick={(clickEvent) =>
                                  clickEvent.stopPropagation()
                                }
                              >
                                <span className="task-list-menu-dot" />
                                <span className="task-list-menu-dot" />
                                <span className="task-list-menu-dot" />
                              </DropdownToggle>
                              <DropdownMenu end>
                                <DropdownItem
                                  className="text-danger"
                                  onClick={(clickEvent) => {
                                    clickEvent.stopPropagation();
                                    openDeleteTaskConfirmation(task.id, status);
                                  }}
                                >
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Infinite Scroll Trigger for Escalation Tasks */}
          {ownershipTab === 'escalated' && (
            <div ref={loadMoreTasksRef} className="task-scroll-trigger">
              {tasksLoading && hasMore && (
                <div className="task-scroll-loading">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2">Loading more tasks...</span>
                </div>
              )}
              {!hasMore && totalElements > 0 && (
                <div className="task-scroll-end">
                  All tasks loaded ({totalElements} total)
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="task-list-empty">
          No tasks match the current filters.
        </div>
      )}
    </div>
  );

  const handleTaskPanelTabChange = (label) => {
    const match = taskOwnershipTabs.find((tab) => tab.label === label);
    if (!match) return;
    setCurrentPage(0); // Reset page when changing tabs
    setOwnershipTab(match.key);
  };

  // Pagination handler
  const handleLoadMore = useCallback(async () => {
    if (tasksLoading || !hasMore) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await fetchTasks({ force: true, pageNo: nextPage, append: true });
  }, [tasksLoading, hasMore, currentPage, fetchTasks]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (ownershipTab !== 'escalated') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !tasksLoading) {
          handleLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentRef = loadMoreTasksRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ownershipTab, hasMore, tasksLoading, handleLoadMore]);

  const showCommentsPanel =
    commentsLoading || commentsError || comments.length > 0;

  const selectedStatusKey = selectedTask?.statusKey || 'pending';
  const selectedStatusLabel =
    STATUS_LABELS[selectedStatusKey] || STATUS_LABELS.pending;
  const resolvedPreviewUrl =
    commentPreviewUrl ||
    (commentAttachmentUrl && isImageUrl(commentAttachmentUrl)
      ? commentAttachmentUrl
      : '');

  const taskDetailsHeader = (
    <div className="task-details-modal-title">
      <span className="task-details-title-text">Task Details</span>
      {selectedTask ? (
        <select
          className={`task-list-select-control task-detail-status task-detail-status-header ${selectedStatusLabel.toLowerCase()}`}
          value={selectedStatusLabel}
          onChange={(changeEvent) => {
            const nextLabel = changeEvent.target.value;
            setSelectedTask((prev) => {
              if (!prev) {
                return prev;
              }

              return {
                ...prev,
                statusKey: STATUS_TO_KEY[nextLabel] || prev.statusKey,
              };
            });
            handleStatusChange(selectedTask.id, selectedStatusKey, nextLabel);
          }}
        >
          {Object.values(STATUS_LABELS).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );

  const taskDetailMetaItems = selectedTask
    ? [
      selectedTask.createdByName
        ? {
          label: 'Created By',
          value: selectedTask.createdByName,
        }
        : null,
      selectedTask.createdById !== null &&
        selectedTask.createdById !== undefined &&
        selectedTask.createdById !== ''
        ? {
          label: 'Created By ID',
          value: selectedTask.createdById,
        }
        : null,
      selectedTask.phoneNumber
        ? {
          label: 'Phone Number',
          value: selectedTask.phoneNumber,
        }
        : null,
      selectedTask.gradeName
        ? {
          label: 'Grade Name',
          value: selectedTask.gradeName,
        }
        : null,
    ].filter(Boolean)
    : [];

  const taskDetailSummary = selectedTask ? (
    <>
      <div className="task-detail-header">
        <div className="task-detail-title-row">
          <h3 className="task-detail-title">{selectedTask.title}</h3>
          <span className="task-detail-date">
            {selectedTask.dueDate ? `Due: ${selectedTask.dueDate}` : 'Due: -'}
          </span>
        </div>
      </div>
      <p className="task-detail-desc">{selectedTask.description}</p>
      {taskDetailMetaItems.length ? (
        <div className="task-detail-meta task-detail-meta-grid">
          {taskDetailMetaItems.map((item) => (
            <div key={item.label} className="task-detail-meta-item">
              <span className="task-detail-meta-label">{item.label}</span>
              <span className="task-detail-meta-value">{item.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </>
  ) : null;

  const taskCommentsSection = (
    <div className="task-detail-comments">
      {showCommentsPanel && (
        <div className="task-comments">
          <div className="task-comments-header">
            <span className="task-comments-title">Comments</span>
            {comments.length > 0 ? (
              <span className="task-comments-count">{comments.length}</span>
            ) : null}
          </div>
          {commentsLoading ? (
            <div className="task-comments-state">Loading comments...</div>
          ) : null}
          {!commentsLoading && commentsError ? (
            <div className="task-comments-state error">{commentsError}</div>
          ) : null}
          {!commentsLoading && !commentsError && comments.length ? (
            <div className="task-comments-list">
              {comments.map((comment) => (
                <div key={comment.id} className="task-comment-item">
                  <div className="task-comment-meta">
                    <div className="task-comment-user">
                      <span className="task-comment-name">
                        {comment.commentedByName || 'User'}
                      </span>
                      {comment.createdAt ? (
                        <span className="task-comment-date">
                          {formatCommentTimestamp(comment.createdAt)}
                        </span>
                      ) : null}
                    </div>
                    <div className="task-comment-actions">
                      <button
                        type="button"
                        className="task-comment-delete"
                        onClick={() => handleDeleteComment(comment.id)}
                        disabled={deletingCommentId === comment.id}
                      >
                        {deletingCommentId === comment.id
                          ? 'Deleting...'
                          : 'Delete'}
                      </button>
                    </div>
                  </div>
                  {comment.comment ? (
                    <p className="task-comment-text">{comment.comment}</p>
                  ) : null}
                  {comment.imageUrl ? (
                    <button
                      type="button"
                      className={`task-comment-attachment ${isImageUrl(comment.imageUrl) ? 'with-thumb' : ''
                        }`}
                      onClick={() => {
                        if (isImageUrl(comment.imageUrl)) {
                          openCommentImagePreview(comment.imageUrl);
                          return;
                        }
                        downloadFileFromUrl(
                          comment.imageUrl,
                          extractFileNameFromUrl(comment.imageUrl),
                        );
                      }}
                    >
                      {isImageUrl(comment.imageUrl) ? (
                        <img
                          src={comment.imageUrl}
                          alt="Attachment"
                          className="task-comment-thumb"
                        />
                      ) : null}
                      <span className="task-comment-attachment-name">
                        {extractFileNameFromUrl(comment.imageUrl)}
                      </span>
                      {isImageUrl(comment.imageUrl) ? null : (
                        <RiDownload2Line size={14} />
                      )}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
      <div className="task-comment-box">
        <div className="task-comment-header">
          <label className="form-label">Add Comment</label>
          <label
            className={`task-upload-btn ${commentUploadLoading ? 'loading' : ''}`}
          >
            <input
              ref={commentFileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              onChange={handleCommentFileChange}
              disabled={commentSubmitting || commentUploadLoading}
            />
            {commentUploadLoading ? 'Uploading...' : 'Choose File'}
          </label>
        </div>
        <textarea
          className="form-control"
          rows="3"
          placeholder="Write a comment..."
          value={commentInput}
          onChange={(changeEvent) => {
            const nextValue = changeEvent.target.value;
            commentDraftRef.current = nextValue;
            setCommentInput(nextValue);
          }}
          disabled={commentSubmitting}
        />
        <div className="task-comment-upload">
          <div className="task-comment-upload-row">
            {resolvedPreviewUrl ? (
              <img
                src={resolvedPreviewUrl}
                alt="Attachment preview"
                className="task-upload-thumb"
              />
            ) : null}
            {commentAttachment || commentAttachmentUrl ? (
              <span className="task-upload-file-name">
                {commentAttachment?.name ||
                  extractFileNameFromUrl(commentAttachmentUrl)}
              </span>
            ) : null}
            {commentAttachmentUrl ? (
              <button
                type="button"
                className="task-upload-download"
                onClick={() =>
                  downloadFileFromUrl(
                    commentAttachmentUrl,
                    extractFileNameFromUrl(commentAttachmentUrl),
                  )
                }
              >
                <RiDownload2Line size={14} />
                Download
              </button>
            ) : null}
            {commentAttachment || commentAttachmentUrl ? (
              <button
                type="button"
                className="task-upload-clear"
                onClick={clearCommentAttachment}
                disabled={commentSubmitting || commentUploadLoading}
              >
                Remove
              </button>
            ) : null}
          </div>
          {commentAttachmentUrl ? (
            <div className="task-upload-url" title={commentAttachmentUrl}>
              {commentAttachmentUrl}
            </div>
          ) : null}
          {commentUploadError ? (
            <div className="task-upload-error">{commentUploadError}</div>
          ) : null}
        </div>
      </div>
      <div className="task-form-actions task-form-actions-right">
        <button
          type="button"
          className="btn btn-outline-secondary task-comment-action"
          onClick={closeTaskDetail}
        >
          Close
        </button>
        <button
          type="button"
          className="btn btn-primary task-comment-action"
          onClick={handleAddComment}
          disabled={
            commentSubmitting || commentUploadLoading || !commentInput.trim()
          }
        >
          Add Comment
        </button>
      </div>
    </div>
  );

  const resolvedViewMode = isEmbedded ? 'list' : viewMode;
  const containerClassName = isEmbedded
    ? 'task-widget task-page font-manrope'
    : 'staff-page task-page font-manrope';

  return (
    <div className={containerClassName}>
      {isCommentImagePreviewOpen && commentImagePreviewUrl ? (
        <div
          className="task-image-preview-backdrop"
          role="button"
          tabIndex={0}
          aria-label="Close image preview"
          onClick={(event) => {
            if (event.target !== event.currentTarget) return;
            closeCommentImagePreview();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              closeCommentImagePreview();
              return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              closeCommentImagePreview();
            }
          }}
        >
          <div className="task-image-preview-card">
            <button
              type="button"
              className="task-image-preview-close"
              aria-label="Close image preview"
              onClick={closeCommentImagePreview}
            >
              ×
            </button>
            <img
              src={commentImagePreviewUrl}
              alt="Comment attachment preview"
              className="task-image-preview-img"
            />
            <div className="task-image-preview-name">
              {extractFileNameFromUrl(commentImagePreviewUrl)}
            </div>
          </div>
        </div>
      ) : null}
      {isEmbedded ? null : (
        <div className="top-head task-head">
          <Heading title="Task Management" />
          <div className="task-head-actions">
            <div className="task-head-icons">
              <AdminNotificationDropdown />
            </div>
            <div
              className="task-view-toggle"
              role="tablist"
              aria-label="Task view"
            >
              <button
                type="button"
                className={`task-view-btn ${resolvedViewMode === 'kanban' ? 'active' : ''
                  }`}
                onClick={() => setViewMode('kanban')}
              >
                Kanban
              </button>
              <button
                type="button"
                className={`task-view-btn ${resolvedViewMode === 'list' ? 'active' : ''
                  }`}
                onClick={() => setViewMode('list')}
              >
                List
              </button>
            </div>
            {canCreateTask ? (
              <button
                type="button"
                className="btn btn-dark"
                onClick={toggleAddTask}
              >
                + Add Task
              </button>
            ) : null}
          </div>
        </div>
      )}

      {isEmbedded ? null : (
        <div className="task-ownership-strip">
          <div className="task-ownership-row">
            <CustomTabs
              tabs={taskOwnershipTabs.map((tab) => tab.label)}
              selectedTab={ownershipTabLabel}
              onTabChange={handleTaskPanelTabChange}
              className="mb-0 task-ownership-nav"
            />
            {ownershipTab === 'created' && assigneeOptions.length > 1 ? (
              <div
                className="task-assignee-filters"
                aria-label="Assignee filter"
              >
                <Select
                  className="task-assignee-select-wrap"
                  classNamePrefix="task-assignee-select"
                  options={assigneeOptions}
                  value={selectedAssigneeFilterOption}
                  onChange={(selectedOption) =>
                    setAssigneeFilter(selectedOption?.value || 'all')
                  }
                  isSearchable={false}
                  menuPortalTarget={
                    typeof document === 'undefined' ? null : document.body
                  }
                  styles={taskAssigneeSelectStyles}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}

      {!detailOnly && (
        <Card
          className={`border-0 card-box task-card-box ${isEmbedded ? 'task-card-embedded' : 'page-card-box'
            }`}
        >
          <CardBody>
            {isEmbedded ? (
              <div className="task-embed-intro">
                <div className="task-embed-header">
                  <h4 className="f-16 fw-semibold mb-2">{embeddedTitle}</h4>
                </div>
                <div className="task-ownership-strip">
                  <div className="task-ownership-row">
                    <CustomTabs
                      tabs={taskOwnershipTabs.map((tab) => tab.label)}
                      selectedTab={ownershipTabLabel}
                      onTabChange={handleTaskPanelTabChange}
                      className="mb-0 task-ownership-nav"
                    />
                    {ownershipTab === 'created' && assigneeOptions.length > 1 ? (
                      <div
                        className="task-assignee-filters"
                        aria-label="Assignee filter"
                      >
                        <Select
                          className="task-assignee-select-wrap"
                          classNamePrefix="task-assignee-select"
                          options={assigneeOptions}
                          value={selectedAssigneeFilterOption}
                          onChange={(selectedOption) =>
                            setAssigneeFilter(selectedOption?.value || 'all')
                          }
                          isSearchable={false}
                          menuPortalTarget={
                            typeof document === 'undefined' ? null : document.body
                          }
                          styles={taskAssigneeSelectStyles}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            <>
              {tasksLoading ? (
                <div className="task-loading">
                  <Loader />
                </div>
              ) : null}
              {!tasksLoading && tasksError ? (
                <div className="task-list-empty">{tasksError}</div>
              ) : null}
              {/* FIX 4: No nested ternary — use extracted render functions */}
              {!tasksLoading &&
                (resolvedViewMode === 'kanban'
                  ? renderKanbanBoard()
                  : renderListView())}
            </>
          </CardBody>
        </Card>
      )}

      <FSBModal
        title={taskDetailsHeader}
        open={isTaskDetailOpen}
        toggleAlert={closeTaskDetail}
        onClose={closeTaskDetail}
        modalClass="right-drawer notification-drawer task-details-drawer"
        message=""
      >
        {selectedTask && (
          <div className="task-detail">
            <div className="task-detail-stack">
              <div className="task-detail-panel task-detail-panel-summary">
                {taskDetailSummary}
              </div>
              <div className="task-detail-panel task-detail-panel-comments">
                {taskCommentsSection}
              </div>
            </div>
          </div>
        )}
        {!selectedTask && detailOnly && (
          <div className="py-4 text-center">
            {!hasLoadedTasks || tasksLoading ? (
              <Loader />
            ) : (
              tasksError || 'Task details not available.'
            )}
          </div>
        )}
      </FSBModal>

      <DeleteConfirmationAlert
        open={showDeleteRecord}
        title="Delete"
        message="Do you really want to remove this record?"
        cancelButtonLabel="Close"
        addButtonLabel="Delete"
        loading={isDeletingTask}
        onConfirm={handleDeleteTask}
        onCancel={closeDeleteTaskConfirmation}
        toggleAlert={closeDeleteTaskConfirmation}
      />

      {isEmbedded ? null : (
        <FSBModal
          title="New To-Do"
          open={isAddTaskOpen}
          toggleAlert={toggleAddTask}
          onClose={toggleAddTask}
          modalClass="task-modal"
          message=""
        >
          <div className="task-form">
            <div className="task-form-field">
              <label className="form-label">
                To-Do <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                type="text"
                placeholder="Enter to-do title"
                value={taskForm.title}
                onChange={(changeEvent) =>
                  updateTaskForm('title', changeEvent.target.value)
                }
              />
            </div>
            <div className="task-form-field">
              <label className="form-label">Details</label>
              <textarea
                className="form-control"
                rows="4"
                placeholder="Add to-do details"
                value={taskForm.details}
                onChange={(changeEvent) =>
                  updateTaskForm('details', changeEvent.target.value)
                }
              />
            </div>
            <div className="task-form-checks">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={taskForm.setDueDate}
                  onChange={toggleDueDate}
                />
                <span className="form-check-label">Set due date</span>
              </label>
            </div>
            {taskForm.setDueDate ? (
              <div className="task-form-field">
                <label className="form-label">
                  Date <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  type="date"
                  value={taskForm.dueDate}
                  onChange={(changeEvent) =>
                    updateTaskForm('dueDate', changeEvent.target.value)
                  }
                />
              </div>
            ) : null}
            {taskForm.setDueDate ? (
              <div className="task-form-field">
                <label className="task-switch" htmlFor="task-repeat-toggle">
                  <input
                    id="task-repeat-toggle"
                    type="checkbox"
                    checked={taskForm.repeatEnabled}
                    onChange={toggleRepeat}
                  />
                  <span className="task-switch-slider" />
                  <span className="task-switch-text">Repeat</span>
                </label>
                {taskForm.repeatEnabled ? (
                  <div className="task-repeat-fields">
                    <div>
                      <label className="form-label">
                        Repeat Every <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={taskForm.repeatEvery}
                        onChange={(changeEvent) =>
                          updateTaskForm(
                            'repeatEvery',
                            changeEvent.target.value,
                          )
                        }
                      >
                        <option value="">Select</option>
                        <option value="Daily">Daily</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Yearly">Yearly</option>
                      </select>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
            {canAssignTask ? (
              <div className="task-form-checks">
                <label className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={taskForm.assignUser}
                    onChange={() =>
                      toggleTaskFormFlag('assignUser', 'assignee')
                    }
                  />
                  <span className="form-check-label">Assign to user</span>
                </label>
              </div>
            ) : null}
            {taskForm.assignUser && canAssignTask ? (
              <div className="task-form-field">
                <label className="form-label">
                  Select User <span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix="react-select"
                  placeholder={
                    staffLoading ? 'Loading users...' : 'Search and select user'
                  }
                  isClearable
                  isSearchable
                  options={staffSelectOptions}
                  value={selectedAssigneeOption}
                  isLoading={staffLoading}
                  isDisabled={staffLoading || Boolean(staffError)}
                  noOptionsMessage={() => {
                    if (staffError) {
                      return 'Unable to load users';
                    }

                    return 'No users found';
                  }}
                  onChange={(selectedOption) =>
                    updateTaskForm('assignee', selectedOption?.value || '')
                  }
                  menuPortalTarget={
                    typeof document === 'undefined' ? null : document.body
                  }
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 2000,
                    }),
                  }}
                />
              </div>
            ) : null}
            <div className="task-form-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  resetTaskForm();
                  toggleAddTask();
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveTask}
                disabled={isSavingTask}
              >
                Save Task
              </button>
            </div>
          </div>
        </FSBModal>
      )}
    </div>
  );
};

export default TaskBoard;
